const chapter = {
  id: 'atomic-structure',
  slug: 'atomic-structure',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Atomic Structure',
        hindi: 'परमाणु संरचना',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'subatomic',
      title: {
        english: 'Discovery of Sub-atomic Particles',
        hindi: 'उप-परमाणवीय कणों की खोज',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Particle', 'Charge', 'Mass', 'Discovery'],
          rows: [
            ['Electron $e^-$', '$-1.6\\times 10^{-19}$ C', '$9.11\\times 10^{-31}$ kg', 'J. J. Thomson — cathode ray tube; $e/m$ measured.'],
            ['Proton $p^+$', '$+1.6\\times 10^{-19}$ C', '$1.673\\times 10^{-27}$ kg', 'Goldstein — anode (canal) rays.'],
            ['Neutron $n$', '$0$', '$1.675\\times 10^{-27}$ kg', 'Chadwick (1932) — Be + α experiment.'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Millikan oil-drop experiment determined charge of electron exactly.',
        hindi: 'मिलिकन तेल-बूंद प्रयोग ने इलेक्ट्रॉन का सटीक चार्ज निर्धारित किया।',
            },
            {
              english: 'Rutherford α-scattering ⇒ nuclear model: dense, positive nucleus; rest of atom mostly empty.',
        hindi: 'रदरफोर्ड α-स्कैटरिंग ⇒ परमाणु मॉडल: सघन, धनात्मक नाभिक; शेष परमाणु अधिकांशतः खाली है।',
            },
            {
              english: 'Atomic number $Z$ = number of protons. Mass number $A = Z + N$ (neutrons).',
        hindi: 'परमाणु क्रमांक $Z$ = प्रोटॉनों की संख्या। द्रव्यमान संख्या $A = Z + N$ (न्यूट्रॉन)।',
            },
          ],
        },
      ],
    },
    {
      id: 'planck-photon',
      title: {
        english: 'Planck\'s Quantum Theory & Photon',
        hindi: 'प्लैंक का क्वांटम सिद्धांत और फोटॉन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Black-body radiation could not be explained classically (UV catastrophe).',
        hindi: 'ब्लैक-बॉडी विकिरण (यूवी आपदा) को शास्त्रीय रूप से समझाया नहीं जा सका।',
            },
            {
              english: '**Planck (1900)**: energy is emitted/absorbed in discrete packets (quanta) of $E = h\\nu$, $h = 6.63\\times 10^{-34}$ J·s.',
        hindi: '**प्लैंक (1900)**: ऊर्जा $E = h\\nu$, $h = 6.63\\times 10^{-34}$ J·s के अलग-अलग पैकेट (क्वांटा) में उत्सर्जित/अवशोषित होती है।',
            },
            {
              english: '**Einstein (1905)** extended this: light itself comes in **photons** of energy $h\\nu$, momentum $h/\\lambda$.',
        hindi: '**आइंस्टीन (1905)** ने इसे बढ़ाया: प्रकाश स्वयं ऊर्जा के **फोटॉन** $h\\nu$, संवेग $h/\\lambda$ में आता है।',
            },
            {
              english: 'Useful: $E$ (in eV) $= 1240/\\lambda$(nm).',
        hindi: 'उपयोगी: $E$ (eV में) $= 1240/\\lambda$(nm)।',
            },
          ],
        },
      ],
    },
    {
      id: 'bohr',
      title: {
        english: 'Bohr Model of Hydrogen-like Atoms',
        hindi: 'हाइड्रोजन सदृश परमाणुओं का बोर मॉडल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Bohr postulates & results',
        hindi: 'बोह्र अभिधारणाएँ और परिणाम',
          },
          statement: {
            english: `Quantisation of angular momentum: $mvr = nh/(2\\pi)$.
**Radius**: $r_n = 0.529\\,n^2/Z$ Å.
**Velocity**: $v_n = (Z/n)\\cdot 2.18\\times 10^6$ m/s.
**Energy**: $E_n = -13.6\\,Z^2/n^2$ eV $= -2.18\\times 10^{-18}\\,Z^2/n^2$ J.
Photon: $h\\nu = E_i - E_f$.`,
            hindi: 'कोणीय गति का परिमाणीकरण: $mvr = nh/(2\\pi)$। **त्रिज्या**: $r_n = 0.529\\,n^2/Z$ Å. **वेग**: $v_n = (Z/n)\\cdot 2.18\\times 10^6$ m/s। **ऊर्जा**: $E_n = -13.6\\,Z^2/n^2$ eV $= -2.18\\times 10^{-18}\\,Z^2/n^2$ जे. फोटॉन: $h\\nu = E_i - E_f$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Rydberg formula (spectrum)',
        hindi: 'रिडबर्ग फॉर्मूला (स्पेक्ट्रम)',
          },
          statement: {
            english: '$\\dfrac{1}{\\lambda} = R Z^2\\!\\left(\\dfrac{1}{n_f^2} - \\dfrac{1}{n_i^2}\\right)$, $R = 1.097\\times 10^7$ m⁻¹.',
        hindi: '$\\dfrac{1}{\\lambda} = R Z^2\\!\\left(\\dfrac{1}{n_f^2} - \\dfrac{1}{n_i^2}\\right)$, $R = 1.097\\times 10^7$ m⁻¹.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Series: Lyman ($n_f=1$, UV), Balmer ($n_f=2$, visible), Paschen ($n_f=3$, IR), Brackett ($n_f=4$), Pfund ($n_f=5$).',
        hindi: 'श्रृंखला: लाइमैन ($n_f=1$, UV), बामर ($n_f=2$, दृश्यमान), पासचेन ($n_f=3$, IR), ब्रैकेट ($n_f=4$), पफंड ($n_f=5$)।',
            },
            {
              english: 'Lines from level $n$: $n(n-1)/2$.',
        hindi: 'स्तर $n$ से पंक्तियाँ: $n(n-1)/2$।',
            },
            {
              english: 'Limitations: only one-electron atoms; cannot explain fine structure, Zeeman, Stark; ignores wave nature.',
        hindi: 'सीमाएं: केवल एक-इलेक्ट्रॉन परमाणु; ठीक संरचना की व्याख्या नहीं कर सकते, ज़ीमन, स्टार्क; तरंग प्रकृति की उपेक्षा करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'dual',
      title: {
        english: 'Dual Nature & Heisenberg Principle',
        hindi: 'द्वैत प्रकृति और हाइजेनबर्ग सिद्धान्त',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**de Broglie**: every particle has wave nature, $\\lambda = h/(mv) = h/\\sqrt{2mK}$.',
        hindi: '**डी ब्रोगली**: प्रत्येक कण में तरंग प्रकृति होती है, $\\lambda = h/(mv) = h/\\sqrt{2mK}$।',
            },
            {
              english: 'For electron through $V$ volts: $\\lambda \\approx 12.27/\\sqrt V$ Å.',
        hindi: '$V$ वोल्ट के माध्यम से इलेक्ट्रॉन के लिए: $\\lambda \\approx 12.27/\\sqrt V$ Å। **हाइजेनबर्ग अनिश्चितता**: $V$। समान रूप से $\\lambda \\approx 12.27/\\sqrt V$.',
            },
            {
              english: '**Heisenberg uncertainty**: $\\Delta x \\cdot \\Delta p \\ge h/(4\\pi)$. Equivalently $\\Delta x \\cdot \\Delta v \\ge h/(4\\pi m)$.',
        hindi: '**हाइजेनबर्ग अनिश्चितता**: $\\Delta x \\cdot \\Delta p \\ge h/(4\\pi)$। समतुल्य $\\Delta x \\cdot \\Delta v \\ge h/(4\\pi m)$.',
            },
            {
              english: 'Implication: classical "orbits" are meaningless — only **probability distributions** (orbitals) are physically defined.',
        hindi: 'निहितार्थ: शास्त्रीय "कक्षाएं" अर्थहीन हैं - केवल **संभावना वितरण** (कक्षाएं) भौतिक रूप से परिभाषित हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'quantum-numbers',
      title: {
        english: 'Quantum Numbers & Orbitals',
        hindi: 'क्वाण्टम संख्याएँ और कक्षक',
      },
      blocks: [
        {
          type: 'table',
          headers: ['QN', 'Symbol', 'Values', 'Determines'],
          rows: [
            ['Principal', '$n$', '$1, 2, 3, \\ldots$', 'Shell, energy, size'],
            ['Azimuthal', '$\\ell$', '$0$ to $n-1$', 'Subshell ($s, p, d, f$), shape, orbital ang. mom.'],
            ['Magnetic', '$m_\\ell$', '$-\\ell$ to $+\\ell$ (incl. 0)', 'Orientation in space; $2\\ell+1$ orbitals'],
            ['Spin', '$m_s$', '$\\pm 1/2$', 'Spin direction'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Orbital angular momentum: $|\\vec L| = \\sqrt{\\ell(\\ell+1)}\\,\\hbar$.',
        hindi: 'कक्षीय कोणीय गति: $|\\vec L| = \\sqrt{\\ell(\\ell+1)}\\,\\hbar$.',
            },
            {
              english: 'Number of orbitals in shell $n$: $n^2$. Maximum electrons in shell: $2n^2$.',
        hindi: 'शेल में ऑर्बिटल्स की संख्या $n$: $n^2$। कोश में अधिकतम इलेक्ट्रॉन: $2n^2$.',
            },
            {
              english: 'Subshell capacities: $s = 2,\\ p = 6,\\ d = 10,\\ f = 14$.',
        hindi: 'उपकोश क्षमताएँ: $s = 2,\\ p = 6,\\ d = 10,\\ f = 14$।',
            },
            {
              english: '**Nodes**: total = $n - 1$; radial = $n - \\ell - 1$; angular = $\\ell$.',
        hindi: '**नोड्स**: कुल = $n - 1$; रेडियल = $n - \\ell - 1$; कोणीय = $\\ell$.',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Shapes: $s$ — spherical; $p$ — dumb-bell ($p_x, p_y, p_z$); $d$ — 4-lobed (4 of them) + $d_{z^2}$ (dumbbell w/ donut); $f$ — complex.',
        hindi: 'आकृतियाँ: $s$ — गोलाकार; $p$ — डम्बल-बेल ($p_x, p_y, p_z$); $d$ - 4-लोबेड (उनमें से 4) + $d_{z^2}$ (डम्बल w/डोनट); $f$ - जटिल।',
            },
          ],
        },
      ],
    },
    {
      id: 'electronic-config',
      title: {
        english: 'Electronic Configuration — Filling Rules',
        hindi: 'इलेक्ट्रॉनिक विन्यास — भरने के नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Aufbau principle**: orbitals fill in order of increasing $(n + \\ell)$; if equal, lower $n$ first. Order: $1s\\,2s\\,2p\\,3s\\,3p\\,4s\\,3d\\,4p\\,5s\\,4d\\,5p\\,6s\\,4f\\,5d\\,6p\\,7s\\ldots$',
        hindi: '**औफबाउ सिद्धांत**: ऑर्बिटल्स बढ़ते क्रम में भरते हैं $(n + \\ell)$; यदि बराबर है, तो पहले $n$ कम करें। आदेश: $1s\\,2s\\,2p\\,3s\\,3p\\,4s\\,3d\\,4p\\,5s\\,4d\\,5p\\,6s\\,4f\\,5d\\,6p\\,7s\\ldots$',
            },
            {
              english: '**Pauli exclusion principle**: no two electrons in an atom have all 4 quantum numbers identical (max 2 e⁻ per orbital, opposite spins).',
        hindi: '**पॉली अपवर्जन सिद्धांत**: किसी परमाणु में किसी भी दो इलेक्ट्रॉनों की सभी 4 क्वांटम संख्याएँ समान नहीं होती हैं (अधिकतम 2 e⁻ प्रति कक्षक, विपरीत स्पिन)।',
            },
            {
              english: '**Hund\'s rule of maximum multiplicity**: in degenerate orbitals (same subshell), electrons fill singly with parallel spins before pairing.',
        hindi: '**अधिकतम बहुलता का हंड का नियम**: पतित कक्षाओं (समान उपकोश) में, इलेक्ट्रॉन युग्मन से पहले समानांतर स्पिन के साथ अकेले भरते हैं।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Anomalous configurations (extra stability of half-filled / filled $d$):',
        hindi: 'असंगत विन्यास (आधे-भरे/भरे $d$ की अतिरिक्त स्थिरता):',
            },
            {
              english: 'Cr ($Z=24$): $[\\text{Ar}] 3d^5 4s^1$ (not $3d^4 4s^2$).',
        hindi: 'सीआर ($Z=24$): $[\\text{Ar}] 3d^5 4s^1$ ($3d^4 4s^2$ नहीं)।',
            },
            {
              english: 'Cu ($Z=29$): $[\\text{Ar}] 3d^{10} 4s^1$ (not $3d^9 4s^2$).',
        hindi: 'Cu ($Z=29$): $[\\text{Ar}] 3d^{10} 4s^1$ ($3d^9 4s^2$ नहीं)।',
            },
            {
              english: 'Similarly Mo, Ag, Au.',
        hindi: 'इसी प्रकार मो, एजी, औ.',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Write the electronic configuration of Fe ($Z=26$) and Fe³⁺.',
        hindi: 'Fe ($Z=26$) और Fe³⁺ का इलेक्ट्रॉनिक विन्यास लिखें।',
          },
          solution: {
            english: 'Fe: $[\\text{Ar}] 3d^6 4s^2$. Fe³⁺ — remove $4s^2$ first then one $3d$: $[\\text{Ar}] 3d^5$.',
        hindi: 'फ़े: $[\\text{Ar}] 3d^6 4s^2$. Fe³⁺ - पहले $4s^2$ हटाएं, फिर एक $3d$: $[\\text{Ar}] 3d^5$ हटाएं।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'How many unpaired electrons are in Cr ($Z = 24$)?',
        hindi: 'Cr ($Z = 24$) में कितने अयुग्मित इलेक्ट्रॉन हैं?',
          },
          solution: {
            english: 'Cr = $[\\text{Ar}] 3d^5 4s^1$ — six unpaired electrons.',
        hindi: 'Cr = $[\\text{Ar}] 3d^5 4s^1$ - छह अयुग्मित इलेक्ट्रॉन।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'r_n = 0.529\\,n^2/Z\\ \\text{Å}',
      desc: {
        english: 'Bohr radius',
        hindi: 'बोहर त्रिज्या',
      },
    },
    {
      latex: 'E_n = -13.6\\,Z^2/n^2\\ \\text{eV}',
      desc: {
        english: 'Energy levels',
        hindi: 'ऊर्जा स्तर',
      },
    },
    {
      latex: '1/\\lambda = R Z^2(1/n_f^2 - 1/n_i^2)',
      desc: {
        english: 'Rydberg',
        hindi: 'रिडबर्ग',
      },
    },
    {
      latex: '\\lambda = h/(mv)',
      desc: {
        english: 'de Broglie',
        hindi: 'डी ब्रोगली',
      },
    },
    {
      latex: '\\Delta x\\,\\Delta p \\ge h/(4\\pi)',
      desc: {
        english: 'Heisenberg',
        hindi: 'हाइजेनबर्ग',
      },
    },
    {
      latex: '|\\vec L| = \\sqrt{\\ell(\\ell+1)}\\,\\hbar',
      desc: {
        english: 'Orbital ang. mom.',
        hindi: 'कक्षीय कोण। माँ।',
      },
    },
    {
      latex: '\\text{Max}\\ e^-\\ \\text{in shell}\\ n = 2n^2',
      desc: {
        english: 'Shell capacity',
        hindi: 'शैल क्षमता',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Bohr',
      problem: {
        english: 'Energy required to excite electron from $n=1$ to $n=2$ in H atom?',
        hindi: 'H परमाणु में $n=1$ से $n=2$ तक इलेक्ट्रॉन को उत्तेजित करने के लिए आवश्यक ऊर्जा?',
      },
      answer: {
        english: '$10.2$ eV',
        hindi: '$10.2$ eV',
      },
      solution: {
        english: '$E_2 - E_1 = -3.4 - (-13.6) = 10.2$ eV.',
        hindi: '$E_2 - E_1 = -3.4 - (-13.6) = 10.2$ eV.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Quantum no.',
      problem: {
        english: 'Total number of orbitals in $n = 4$ shell?',
        hindi: '$n = 4$ शेल में ऑर्बिटल्स की कुल संख्या?',
      },
      answer: {
        english: '$16$',
        hindi: '$16$',
      },
      solution: {
        english: '$n^2 = 16$ (1 s + 3 p + 5 d + 7 f).',
        hindi: '$n^2 = 16$ (1 एस + 3 पी + 5 डी + 7 एफ)।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Spectrum',
      problem: {
        english: 'Wavelength of first Balmer line of H atom?',
        hindi: 'H परमाणु की पहली बामर रेखा की तरंगदैर्घ्य?',
      },
      answer: {
        english: '$\\approx 656$ nm (H$_\\alpha$)',
        hindi: '$\\approx 656$ एनएम (H$_\\alpha$)',
      },
      solution: {
        english: '$1/\\lambda = R(1/4 - 1/9) = 5R/36$.',
        hindi: '$1/\\lambda = R(1/4 - 1/9) = 5R/36$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Aufbau',
      problem: {
        english: 'Electronic configuration of Cu ($Z = 29$)?',
        hindi: 'Cu ($Z = 29$) का इलेक्ट्रॉनिक विन्यास?',
      },
      answer: {
        english: '$[\\text{Ar}] 3d^{10} 4s^1$',
        hindi: '$[\\text{Ar}] 3d^{10} 4s^1$',
      },
      solution: {
        english: 'Anomalous — extra stability of fully-filled $d$.',
        hindi: 'असंगत - पूरी तरह से भरे हुए $d$ की अतिरिक्त स्थिरता।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'de Broglie',
      problem: {
        english: 'de Broglie wavelength of electron accelerated through 100 V?',
        hindi: '100 V के माध्यम से त्वरित इलेक्ट्रॉन की डी ब्रोगली तरंग दैर्ध्य?',
      },
      answer: {
        english: '$1.227$ Å',
        hindi: '$1.227$ Å',
      },
      solution: {
        english: '$\\lambda = 12.27/\\sqrt{100}$.',
        hindi: '$\\lambda = 12.27/\\sqrt{100}$.',
      },
    },
  ],
  next: {
    slug: 'chemical-bonding',
    title: 'Chemical Bonding & Molecular Structure',
  },
};

export default chapter;
